<script setup lang="ts">
import { useClipboard } from '@vueuse/core'

const open = defineModel<boolean>({ required: true })

const { enabled: aiScanEnabled } = useFlag('AI_SCAN')

const { copy, copied } = useClipboard({ legacy: true, copiedDuring: 1500 })

const CLI_INSTALL_COMMAND =
  'curl -fsSL https://raw.githubusercontent.com/sleuth-io/sx/main/install.sh | bash && sx init --type sleuth'

const mcpUrl = computed(() => {
  if (typeof window === 'undefined') return ''
  return `${window.location.origin}/mcp/assets`
})

const tabItems = [
  {
    slot: 'cli' as const,
    label: 'Install for CLI',
    icon: 'light:rectangle-terminal',
  },
  {
    slot: 'cloud' as const,
    label: 'Install for Cloud LLMs',
    icon: 'light:cloud-arrow-up',
  },
]
</script>

<template>
  <UModal v-model="open" :ui="{ width: 'w-full sm:max-w-2xl' }">
    <ModalContentWrapper
      title="How to set up"
      :subtitle="
        aiScanEnabled
          ? 'Install a client to collect your AI usage, and keep it synced.'
          : 'Connect skills.new to your local tools and cloud LLMs.'
      "
      @close="open = false"
    >
      <AiscanSetupInstructions v-if="aiScanEnabled" />
      <UTabs v-else :items="tabItems" class="w-full">
        <template #cli>
          <div class="space-y-6 pt-4">
            <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Install the sx command-line application to install AI assets for local CLI LLMs such as Claude Code,
              Codex, Cursor, Gemini-cli, Kiro and more.
            </div>
            <div>
              <h3 class="mb-3 text-lg font-semibold">Step 1: Run the installation command</h3>
              <div class="rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <div class="flex items-center justify-between gap-2">
                  <code
                    class="flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm text-gray-800 scrollbar-none dark:text-gray-200"
                  >
                    {{ CLI_INSTALL_COMMAND }}
                  </code>
                  <UButton
                    :icon="copied ? 'light:check' : 'light:copy'"
                    variant="ghost"
                    size="sm"
                    @click="copy(CLI_INSTALL_COMMAND)"
                  />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Open your terminal and run this command to install and initialize the Skills CLI.
              </p>
            </div>

            <div>
              <h3 class="mb-3 text-lg font-semibold">Step 2: Complete the authentication process</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                The CLI will guide you through the authentication process. Once completed, you can manage your skills
                from the command line.
              </p>
            </div>
          </div>
        </template>

        <template #cloud>
          <div class="space-y-6 pt-4">
            <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              To use AI assets in cloud LLMs like claude.ai and chatgpt.com you need to install an MCP that will
              dynamically deliver the assets for the LLM to use.
            </div>
            <div>
              <h3 class="mb-3 text-lg font-semibold">MCP Server URL</h3>
              <div class="rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <div class="flex items-center justify-between gap-2">
                  <code
                    class="flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm text-gray-800 scrollbar-none dark:text-gray-200"
                  >
                    {{ mcpUrl }}
                  </code>
                  <UButton
                    :icon="copied ? 'light:check' : 'light:copy'"
                    variant="ghost"
                    size="sm"
                    @click="copy(mcpUrl)"
                  />
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Provide this URL when adding a new MCP server in a cloud LLM. You'll be prompted to sign in through your
                browser the first time the connector is used.
              </p>
            </div>

            <div>
              <h3 class="mb-2 text-lg font-semibold">claude.ai</h3>
              <ol class="list-decimal space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-400">
                <li>Open <span class="font-medium">Settings &rarr; Connectors</span>.</li>
                <li>Click <span class="font-medium">Add custom connector</span>.</li>
                <li>Paste the MCP Server URL above and save.</li>
                <li>When prompted, click <span class="font-medium">Connect</span> and sign in to authenticate.</li>
              </ol>
            </div>

            <div>
              <h3 class="mb-2 text-lg font-semibold">chatgpt.com</h3>
              <ol class="list-decimal space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-400">
                <li>
                  Open the
                  <NuxtLink
                    :to="chatgptAppUrl"
                    target="_blank"
                    external
                    rel="noopener noreferrer"
                    class="font-medium text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Sleuth Skills app
                  </NuxtLink>
                  in the ChatGPT app store, or search for
                  <span class="font-medium">Sleuth Skills</span> under
                  <span class="font-medium">Settings &rarr; Apps</span>.
                </li>
                <li>
                  Click <span class="font-medium">Connect</span> and complete the OAuth sign-in to authorize access.
                </li>
              </ol>
            </div>

            <div
              class="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-100"
            >
              Authentication is per-user — each teammate who adds the connector will sign in with their own account and
              will only see skills they have access to.
            </div>
          </div>
        </template>
      </UTabs>
    </ModalContentWrapper>
  </UModal>
</template>
